import React from 'react';
import { useContext, useEffect } from 'react';
import { AdobeClickType, CLAIM_ASSESSMENT_STATUS, CLAIM_STATUS_TYPE } from '../../constants/constants';
import Card from '../../common/components/card';
import {
    GelScreenDetectorContext,
    GelContainerLite,
    GelParagraph,
    GelHeading2,
    GelSpinner,
    GelRowLayout,
    GelButton,
    GelHeading6
} from '@tal-gel/components';
import Requirement from './requirement';
import BenefitType from './benefitType';
import NextPayment from './nextPayment';
import { getGelTokens } from '@tal-gel/theming';
import { UserContext } from '../../common/usercontext/user.context';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import DocumentUpload from './documentupload';
import ResponsiveContainer from "../../components/responsive.container";
import { AdobeAnalytics } from '../../common/analytics/adobe-analytics';
import { useGetHomePageContent } from "../../common/api/graphQLDataFetch"
import { useCookies } from 'react-cookie';
import { ContentfulAlert } from './contentfulAlert';
import { MemberClaims } from '../../common/models/memberclaims';
import { getNextScheduledPayment } from '../payments/helpers';

const ClaimsContainer = () => {
    const { global: { sizeNone, themeColorBackgroundLight, sizeBaseX15, sizeBaseX5, 
        sizeBaseX10, sizeBaseX3, themeColorBorderDefault, sizeBaseUnit, 
        themeColorBackgroundDefault, sizeBaseX4, themeColorGrayT20 } } = getGelTokens();
    const { data, isFetched } = useGetHomePageContent()

    const navigate = useNavigate();
    const { screen, isXsScreen } = useContext(GelScreenDetectorContext) as GelScreenDetectorContext;
    const { contextData } = useContext(UserContext);
    const [{ tpid }] = useCookies();

    const ClaimsJourneyHeader = styled.div(() => ({
        minHeight: sizeBaseUnit * 15,
        padding: sizeBaseX4,
        borderBottom: `1px solid ${themeColorBorderDefault}`,
        borderRadius: `${sizeBaseUnit * 2}px ${sizeBaseUnit * 2}px 0px 0px`
    }));

    const ClaimsJourneyBody = styled.div(() => ({
        backgroundColor: themeColorBackgroundDefault,
        border: "1px solid",
        borderColor: themeColorGrayT20,
        padding: "0px",
        borderRadius: sizeBaseUnit * 2,
    }));

    if (contextData.memberDetails?.memberNumber &&
        (contextData?.claims?.length === 0 || !contextData?.claims)) {
        navigate("/error");
    }

    useEffect(() => {
        if (tpid) {
            AdobeAnalytics.PushFormStartEvent(tpid);
            AdobeAnalytics.PushPageViewEvent(tpid);
        }
    }, [tpid]);

    const PageHeader = styled.div(() => ({
        padding: isXsScreen(screen) ? `${sizeBaseX15}px ${sizeBaseX5}px` : `${sizeBaseX15}px ${sizeBaseX10}px`,
        backgroundColor: themeColorBackgroundLight
    }));

    const onClickClaimsJourney = () => {
        AdobeAnalytics.PushClickEvent(tpid, data?.claimsJourneyHeader!, AdobeClickType.Button);
        navigate("/help?", { state: { selectedSubSection: 0, selectedQuestion: 0, isFromHomePage: true } });
    };   

    if (contextData.memberDetails?.displayLegalDisclosure)
        navigate("/yourDeclaration");


    const claimPadding = (index: number) =>
        isXsScreen(screen)
          ? `0px ${sizeBaseX5}px ${sizeBaseX5}px ${sizeBaseX5}px`
          : `0px ${sizeBaseX10}px ${index === contextData.claims.length - 1 ? sizeNone : sizeBaseX5}px ${sizeBaseX10}px`;      

    if (!isFetched) {
        <GelSpinner medium overlay />;
    }

    const getClaimSortPriority = (claim: MemberClaims) => {
        if (claim.status.description.includes(CLAIM_STATUS_TYPE.RTS)) return 1;
        if (claim.status.description === CLAIM_STATUS_TYPE.CL) return 2;
        return 0;
    };

    return (       
        <GelContainerLite style={{ padding: sizeNone, overflow: "hidden" }}>
            {contextData.claims?.length > 0 &&
                <GelContainerLite style={{ padding: sizeNone }}>
                    <ContentfulAlert alerts={data?.alertDetails}/>
                    <PageHeader >
                        <GelHeading2>{data?.appTitle}</GelHeading2>
                        <GelParagraph style={{ paddingTop: sizeBaseX3 }}>{data?.appTitleDescription}</GelParagraph>
                    </PageHeader>
                </GelContainerLite>
            }
            <ResponsiveContainer style={{ paddingTop: `${sizeBaseX15}px`, paddingBottom: `${sizeBaseX15}px` }}>
                {contextData
                    ?.claims
                    ?.sort((a, b) => getClaimSortPriority(a) - getClaimSortPriority(b))
                    ?.map((claim, index) => (
                        <div key={claim.claimNo} style={{ padding: claimPadding(index) }} >
                            <Card title={claim.claimTypeName} claimAssesmentStatus={claim?.claimAssesmentStatus}
                                buttonText={data?.buttonText} icon={"InfoCircle"}
                                showFooter={true} claimNumber={claim.caseNumber}>
                                <BenefitType claimNumber={claim.caseNumber} description={claim?.status.description} friendlyStatus={claim?.status.friendlyStatus}
                                    tooltipText={claim?.status.tooltipText}
                                    benefitTypeText={claim.claimTypeName} isFromHomePage={true} />

                                {(claim?.claimAssesmentStatus !== CLAIM_ASSESSMENT_STATUS.RN) &&
                                    <Requirement 
                                        requirementList={claim?.status?.description === CLAIM_STATUS_TYPE.CL ? [] : claim.requirements.filter(rq => rq.type !== 1 && rq.type !== 3 && rq.type !== 4)}
                                        thirdPartyRequirement={claim?.status.thirdPartyRequirementInfoText} 
                                        caseNumber={claim.caseNumber}/>}

                                {(claim?.claimAssesmentStatus !== CLAIM_ASSESSMENT_STATUS.RN) &&
                                    <NextPayment message={data?.paymentMessage!}
                                        nextScheduledPayment={getNextScheduledPayment(claim.claimId,contextData?.scheduledPayments)} />}

                                {claim?.claimAssesmentStatus === CLAIM_ASSESSMENT_STATUS.RN && (
                                    <DocumentUpload
                                        title={claim.claimTypeName}
                                        caseNumber={claim.caseNumber}
                                        uploadDocumentDescriptor={
                                            claim.isLodgementTypeTele
                                                ? data?.teleclaimMessage
                                                : data?.uploadDocumentDescriptor
                                        }
                                        uploadDocumentText={
                                            claim.isLodgementTypeTele
                                                ? ''
                                                : data?.uploadDocumentText
                                        }
                                    />
                                )}
                            </Card>
                        </div>
                    ))}

                {contextData?.claims?.length > 0 && data && (
                    <GelRowLayout style={{ padding: isXsScreen(screen)? "0px 20px 20px":"20px 40px"}}>
                        <ClaimsJourneyBody>
                            <ClaimsJourneyHeader >
                                <GelHeading6>{data.claimsJourneyHeader}</GelHeading6>
                            </ClaimsJourneyHeader>

                            <GelParagraph style={{ padding: "16px" }}>{data.claimsJourneyContent}</GelParagraph>

                            <GelRowLayout style={{ padding: "0px 16px 16px 16px" }}>
                                <GelButton
                                    name=""
                                    tertiary small onClick={onClickClaimsJourney}>
                                    {data.claimsJourneyButtonText}
                                </GelButton>
                            </GelRowLayout>
                        </ClaimsJourneyBody>
                    </GelRowLayout>
                )}
            </ResponsiveContainer>
        </GelContainerLite>
    );
};

export default ClaimsContainer;